import {
  AudioVideoObserver,
  ClientMetricReport,
  EventAttributes,
  EventName,
  EventObserver,
  MeetingSession,
  Versioning,
} from 'amazon-chime-sdk-js';

import {
  AvTelemetrySink,
  AvTelemetrySource,
} from 'src/app/features/video-conference/av-telemetry/models/av-telemetry-source';
import {
  AvTelemetryCadenceMode,
  AvTelemetryClientHints,
  AvTelemetryStorageEstimate,
  AvTelemetryVendor,
} from 'src/app/features/video-conference/av-telemetry/models/av-telemetry.model';
import {
  CHIME_MEETING_HISTORY_WINDOW_MS,
  ChimeEventPayload,
  ChimeHistoryEntry,
  ChimeMetaPayload,
  ChimeSyntheticEventName,
  ChimeTickPayload,
} from 'src/app/features/video-conference/chime/models/av-telemetry.model';

export class ChimeAvTelemetrySource implements AvTelemetrySource {
  readonly vendor: AvTelemetryVendor = 'chime';
  readonly vendorVersion: string = Versioning.sdkVersion;
  readonly cadenceMode: AvTelemetryCadenceMode = 'push';

  private sink: AvTelemetrySink | null = null;
  private audioVideoObserver: AudioVideoObserver | null = null;
  private eventObserver: EventObserver | null = null;

  constructor(
    private readonly meetingSession: MeetingSession,
    private readonly mediaRegion: string | null,
    private readonly storageEstimate: AvTelemetryStorageEstimate | null = null
  ) {}

  describeClient(): ChimeMetaPayload {
    const navigatorRef = typeof navigator === 'undefined' ? null : navigator;
    const screenRef = typeof screen === 'undefined' ? null : screen;

    return {
      meetingId: this.meetingId,
      attendeeId: this.attendeeId,
      mediaRegion: this.mediaRegion,
      userAgent: navigatorRef?.userAgent ?? '',
      clientHints: this.readLowEntropyClientHints(),
      language: navigatorRef?.language ?? '',
      hardwareConcurrency: navigatorRef?.hardwareConcurrency ?? null,
      // Chromium-only; absent elsewhere, which is itself a useful signal.
      deviceMemoryGb: (navigatorRef as { deviceMemory?: number })?.deviceMemory ?? null,
      screenWidth: screenRef?.width ?? null,
      screenHeight: screenRef?.height ?? null,
      devicePixelRatio: typeof window === 'undefined' ? null : (window.devicePixelRatio ?? null),
      storageQuotaBytes: this.storageEstimate?.quotaBytes ?? null,
      storageUsageBytes: this.storageEstimate?.usageBytes ?? null,
    };
  }

  attach(sink: AvTelemetrySink): void {
    this.sink = sink;

    this.audioVideoObserver = {
      metricsDidReceive: (clientMetricReport: ClientMetricReport) => {
        this.sink?.reportTick(this.toTickPayload(clientMetricReport));
      },
      connectionDidBecomePoor: () => {
        this.reportSyntheticEvent(ChimeSyntheticEventName.ConnectionDidBecomePoor);
      },
      connectionDidBecomeGood: () => {
        this.reportSyntheticEvent(ChimeSyntheticEventName.ConnectionDidBecomeGood);
      },
    };

    this.eventObserver = {
      eventDidReceive: (name: EventName, attributes: EventAttributes) => {
        this.sink?.reportEvent({
          meetingId: this.meetingId,
          name: name,
          attributes: this.withoutMeetingHistory(attributes),
          history: this.trimMeetingHistory(attributes),
        } satisfies ChimeEventPayload);
      },
    };

    this.meetingSession.audioVideo.addObserver(this.audioVideoObserver);
    this.meetingSession.eventController?.addObserver(this.eventObserver);

    // Metrics still flow without an eventController, but discrete events do not.
    if (!this.meetingSession.eventController) {
      this.reportSyntheticEvent(ChimeSyntheticEventName.EventControllerUnavailable);
    }
  }

  detach(): void {
    if (this.audioVideoObserver) {
      this.meetingSession.audioVideo.removeObserver(this.audioVideoObserver);
      this.audioVideoObserver = null;
    }

    if (this.eventObserver) {
      this.meetingSession.eventController?.removeObserver(this.eventObserver);
      this.eventObserver = null;
    }

    this.sink = null;
  }

  private get meetingId(): string {
    return this.meetingSession.configuration?.meetingId ?? '';
  }

  private get attendeeId(): string {
    return this.meetingSession.configuration?.credentials?.attendeeId ?? '';
  }

  private readLowEntropyClientHints(): AvTelemetryClientHints {
    const uaData = typeof navigator === 'undefined' ? null : navigator.userAgentData;

    if (!uaData) {
      return { brands: null, mobile: null, platform: null };
    }

    return {
      brands: uaData.brands?.map((entry) => ({ brand: entry.brand, version: entry.version })) ?? null,
      mobile: uaData.mobile ?? null,
      platform: uaData.platform ?? null,
    };
  }

  private toTickPayload(clientMetricReport: ClientMetricReport): ChimeTickPayload {
    return {
      meetingId: this.meetingId,
      session: clientMetricReport.getObservableMetrics(),
      video: clientMetricReport.getObservableVideoMetrics(),
    };
  }

  private reportSyntheticEvent(name: ChimeSyntheticEventName): void {
    this.sink?.reportEvent({
      meetingId: this.meetingId,
      name: name,
      attributes: {},
    } satisfies ChimeEventPayload);
  }

  private trimMeetingHistory(attributes: EventAttributes): ChimeHistoryEntry[] {
    const history = attributes?.meetingHistory;
    if (!history?.length) {
      return [];
    }

    const cutoffMs = history[history.length - 1].timestampMs - CHIME_MEETING_HISTORY_WINDOW_MS;

    return history
      .filter((entry) => entry.timestampMs >= cutoffMs)
      .map((entry) => ({ name: entry.name, timestampMs: entry.timestampMs }));
  }

  private withoutMeetingHistory(attributes: EventAttributes): Record<string, unknown> {
    const { meetingHistory, ...rest } = (attributes ?? {}) as EventAttributes &
      Record<string, unknown>;

    return rest;
  }
}
